import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import { useAuth } from '../contexts/AuthContext';
import { useAdFree } from '../hooks/useAdFree';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { AlbumArt } from '../components/player/AlbumArt';
import { NowPlaying } from '../components/player/NowPlaying';
import { Controls } from '../components/player/Controls';
import { ProgressBar } from '../components/player/ProgressBar';
import { AudioVisualizer } from '../components/player/AudioVisualizer';
import { SongHistory } from '../components/player/SongHistory';
import { UpNext } from '../components/player/UpNext';
import { StationSwitcher } from '../components/player/StationSwitcher';
import { ListenerCount } from '../components/player/ListenerCount';
import { ShareButtons } from '../components/player/ShareButtons';
import { StationInfo } from '../components/player/StationInfo';
import { SongRequest } from '../components/player/SongRequest';
import { DJRequestQueue } from '../components/player/DJRequestQueue';
import { Weather } from '../components/player/Weather';
import { Donation } from '../components/player/Donation';
import { LiveVideo } from '../components/player/LiveVideo';
import { Chat } from '../components/player/Chat';
import { ThemeSettings } from '../components/settings/ThemeSettings';
import { SponsorBanner } from '../components/ads/SponsorBanner';
import { PreRollSplash } from '../components/ads/PreRollSplash';
import { DJShoutout } from '../components/ads/DJShoutout';
import { Leaderboard } from '../components/player/Leaderboard';
import { Dedications } from '../components/player/Dedications';
import { PWAInstallPrompt } from '../components/pwa/PWAInstallPrompt';
import { PushNotificationToggle } from '../components/pwa/PushNotificationToggle';
import { NewsTicker } from '../components/player/NewsTicker';
import { DiscordCallIn } from '../components/player/DiscordCallIn';
import { Menu, X, Radio, RefreshCw, History, Share2, Music, Info, Heart, Trophy, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

export function RadioPlayer() {
  const navigate = useNavigate();
  const { stationData, refreshStream, error, isRefreshing } = useAudio();
  const { user } = useAuth();
  const { adFree } = useAdFree();
  useKeyboardShortcuts(); // Enable keyboard shortcuts
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const [showPreRoll, setShowPreRoll] = useState(true);
  const wsRef = useRef(null);
  const station = stationData?.station;
  
  // Check if user is DJ/Admin/Mod
  const isDJ = user && ['dj', 'admin', 'mod'].includes(user.role);
  const isAdmin = user && ['admin', 'mod'].includes(user.role);

  // Show pre-roll splash on first visit
  if (showPreRoll) {
    return <PreRollSplash adFree={adFree} onComplete={() => setShowPreRoll(false)} />;
  }

  const tabs = [
    { id: 'history', label: 'History', icon: History },
    { id: 'request', label: 'Request', icon: Music },
    { id: 'dedicate', label: 'Dedicate', icon: Heart },
    { id: 'share', label: 'Share', icon: Share2 },
    { id: 'leaderboard', label: 'Top Fans', icon: Trophy },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'history':
        return (
          <div className="space-y-4">
            <DJShoutout />
            <SongHistory />
            <Chat wsRef={wsRef} />
          </div>
        );
      case 'request':
        return (
          <div className="space-y-4">
            {isDJ && <DJRequestQueue />}
            <SongRequest />
          </div>
        );
      case 'dedicate':
        return <Dedications wsRef={wsRef} />;
      case 'share':
        return (
          <div className="space-y-4">
            <DiscordCallIn />
            <ShareButtons />
            <StationInfo />
            <PushNotificationToggle />
          </div>
        );
      case 'leaderboard':
        return (
          <div className="space-y-4">
            <Leaderboard />
            <Donation />
            <Weather />
          </div>
        );
      default:
        return <SongHistory />;
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: 'var(--background)' }}
      data-testid="radio-player"
    >
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Background gradient */}
      <div 
        className="fixed inset-0 opacity-30"
        style={{
          background: `radial-gradient(ellipse at 20% 50%, var(--primary) 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 80%, var(--secondary) 0%, transparent 50%)`
        }}
      />

      {/* Header */}
      <header 
        className="relative z-10 flex items-center justify-between px-4 md:px-8 py-4"
        data-testid="player-header"
      >
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_67da558a-9e84-415d-a51c-097c2aae8691/artifacts/tg6gwsri_ChatGPT%20Image%20Feb%2027%2C%202026%2C%2002_12_10%20PM.png"
            alt="DJ's & MC Live"
            className="w-12 h-12 rounded-xl object-contain"
          />
          <div>
            <h1 
              className="text-xl font-bold leading-none"
              style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
              data-testid="station-name"
            >
              DJ's & MC Live
            </h1>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-muted)' }}
            >
              {station?.description || "Nashville's #1 Internet Radio"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Admin Button - only for admin/mod */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="rounded-full"
              style={{ 
                backgroundColor: 'var(--surface-highlight)',
                color: '#FFD700'
              }}
              data-testid="admin-button"
              title="Admin Dashboard"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={refreshStream}
            disabled={isRefreshing}
            className="rounded-full"
            style={{ 
              backgroundColor: 'var(--surface-highlight)',
              color: 'var(--text-main)'
            }}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>

          <ThemeSettings />

          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                style={{ 
                  backgroundColor: 'var(--surface-highlight)',
                  color: 'var(--text-main)'
                }}
                data-testid="mobile-menu-button"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-80 p-0 border-l overflow-hidden"
              style={{ 
                backgroundColor: 'var(--surface)',
                borderColor: 'var(--border-color)'
              }}
            >
              <div className="p-4 space-y-4 h-full overflow-y-auto pb-20">
                <LiveVideo />
                <UpNext />
                <SongHistory />
                <Chat />
                <ShareButtons />
                <SongRequest />
                <Donation />
                <Weather />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div 
          className="relative z-10 mx-4 md:mx-8 mb-4 p-3 rounded-lg text-center"
          style={{ 
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            color: '#EF4444'
          }}
          data-testid="error-banner"
        >
          {error}
        </div>
      )}

      {/* Main content */}
      <main className="relative z-10 flex flex-col lg:flex-row flex-1 overflow-hidden">
        {/* Left panel - Player */}
        <div className="flex-1 flex flex-col items-center p-4 md:p-6 lg:p-8 pb-48 lg:pb-8 overflow-y-auto">
          <div className="w-full max-w-xl space-y-4">
            {/* Live Video - desktop only, mobile users access via menu */}
            <div className="hidden lg:block">
              <LiveVideo />
            </div>
            
            <div className="flex justify-center">
              <AlbumArt size="large" />
            </div>
            <NowPlaying />
            <ProgressBar />
            {/* Visualizer and Controls - hide on mobile (shown in bottom bar instead) */}
            <div className="hidden lg:flex justify-center">
              <AudioVisualizer width={350} height={70} barCount={35} />
            </div>
            <div className="hidden lg:block">
              <Controls size="large" />
            </div>
          </div>
        </div>

        {/* Right panel - Sidebar (desktop only) */}
        <aside 
          className="hidden lg:flex lg:flex-col w-96 border-l overflow-y-auto"
          style={{ 
            backgroundColor: 'var(--surface)',
            borderColor: 'var(--border-color)'
          }}
          data-testid="desktop-sidebar"
        >
          {/* Up Next - always visible (now includes clock) */}
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <UpNext />
          </div>

          {/* Tabs with Clock */}
          <div 
            className="flex items-center border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div className="flex-1 flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-1 flex flex-col items-center gap-1 py-3 transition-all"
                    style={{ 
                      backgroundColor: isActive ? 'var(--surface-highlight)' : 'transparent',
                      color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                      borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent'
                    }}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {renderTabContent()}
          </div>

          {/* Sponsor Banner - at bottom of sidebar */}
          <div className="p-4 pb-20 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <SponsorBanner adFree={adFree} />
          </div>
        </aside>
      </main>

      {/* Mobile bottom bar */}
      <div 
        className="lg:hidden fixed bottom-8 left-0 right-0 glass border-t p-4 pb-6 z-50"
        style={{ borderColor: 'var(--border-color)' }}
        data-testid="mobile-bottom-bar"
      >
        <div className="flex flex-col items-center gap-2 max-w-xl mx-auto">
          <AudioVisualizer width={280} height={40} barCount={28} />
          <Controls size="medium" />
        </div>
      </div>

      {/* News Ticker */}
      <NewsTicker />

      {/* Custom Footer - Snapdismedia branding */}
      <div 
        className="fixed z-[9999]"
        style={{ 
          bottom: '45px', 
          right: '16px'
        }}
      >
        <a 
          href="https://snapdismedia.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.85)', 
            color: '#fff',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          <span style={{ opacity: 0.7 }}>Powered by</span>
          <span style={{ color: '#8B5CF6', fontWeight: 'bold' }}>Snapdismedia</span>
        </a>
      </div>
    </div>
  );
}
